import React from "react";
import {
  ShieldCheck,
  Landmark,
  KeyRound,
  Fingerprint,
  MoreVertical,
  Plus,
  Info,
} from "lucide-react";
import "./DigitalIdentity.css";

const identities = [
  {
    id: 1,
    name: "Demo Admin",
    email: "admin@intrust.com",
    certType: "Digital Certificate (PQ)",
    certClass: "pq",
    validTill: "Valid till May 18, 2028",
    icon: "shield",
    iconColor: "purple",
    isDefault: true,
  },
  {
    id: 2,
    name: "Demo Admin (Classical)",
    email: "admin.classical@intrust.com",
    certType: "Classical Certificate",
    certClass: "classical",
    validTill: "Valid till Apr 10, 2027",
    icon: "landmark",
    iconColor: "gray",
    isDefault: false,
  },
  {
    id: 3,
    name: "Demo Admin (Hybrid)",
    email: "admin.hybrid@intrust.com",
    certType: "Hybrid Certificate (PQ + Classical)",
    certClass: "hybrid",
    validTill: "Valid till May 05, 2028",
    icon: "key",
    iconColor: "indigo",
    isDefault: false,
  },
  {
    id: 4,
    name: "Hardware Identity (YubiKey)",
    email: "admin.yubikey@intrust.com",
    certType: "Hardware Security Module",
    certClass: "hardware",
    validTill: "Valid till May 16, 2028",
    icon: "fingerprint",
    iconColor: "blue",
    isDefault: false,
  },
];

function IdentityIcon({ type, size = 20 }) {
  switch (type) {
    case "landmark":
      return <Landmark size={size} strokeWidth={2} />;
    case "key":
      return <KeyRound size={size} strokeWidth={2} />;
    case "fingerprint":
      return <Fingerprint size={size} strokeWidth={2} />;
    case "shield":
    default:
      return <ShieldCheck size={size} strokeWidth={2} />;
  }
}

export default function DigitalIdentity() {
  const defaultIdentity = identities.find((i) => i.isDefault);

  return (
    <div className="di-page">
      <div className="di-container">
        {/* Default Digital Identity */}
        <section className="di-section">
          <div className="di-section-head">
            <div>
              <h2 className="di-section-title">Default Digital Identity</h2>
              <p className="di-section-subtitle">
                This identity will be used for signing documents by default.
              </p>
            </div>
            <button className="di-add-btn">
              <Plus size={15} strokeWidth={2.5} />
              <span>Add Identity</span>
            </button>
          </div>

          <div className="di-default-card">
            <div className="di-identity-left">
              <div className={`di-icon-wrap ${defaultIdentity.iconColor}`}>
                <IdentityIcon type={defaultIdentity.icon} />
              </div>
              <div>
                <p className="di-identity-name">{defaultIdentity.name}</p>
                <p className="di-identity-email">{defaultIdentity.email}</p>
              </div>
            </div>

            <div className="di-identity-meta">
              <p className={`di-cert-type ${defaultIdentity.certClass}`}>
                {defaultIdentity.certType}
              </p>
              <p className="di-cert-valid">{defaultIdentity.validTill}</p>
            </div>

            <button className="di-set-default-btn">Set as Default</button>
          </div>
        </section>

        {/* All Digital Identities */}
        <section className="di-section">
          <div className="di-section-head">
            <h2 className="di-section-title">All Digital Identities</h2>
          </div>

          <div className="di-list">
            {identities.map((identity) => (
              <div className="di-list-row" key={identity.id}>
                <div className="di-identity-left">
                  <div className={`di-icon-wrap ${identity.iconColor}`}>
                    <IdentityIcon type={identity.icon} />
                  </div>
                  <div>
                    <p className="di-identity-name">{identity.name}</p>
                    <p className="di-identity-email">{identity.email}</p>
                  </div>
                </div>

                <div className="di-identity-meta">
                  <p className={`di-cert-type ${identity.certClass}`}>
                    {identity.certType}
                  </p>
                  <p className="di-cert-valid">{identity.validTill}</p>
                </div>

                <div className="di-row-right">
                  {identity.isDefault ? (
                    <span className="di-default-badge">Default</span>
                  ) : (
                    <span style={{ width: 0 }} />
                  )}
                  <button className="di-menu-btn" aria-label="More options">
                    <MoreVertical size={16} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="di-info-note">
            <Info size={16} strokeWidth={2} />
            <span>
              Digital identities are backed by certificates and are used to
              create legally valid digital signatures.
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}